import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  AttachMoney,
  People,
  Work,
  CloudUpload,
  CheckCircle,
  ContentCopy,
  Check,
  LinkedIn
} from '@mui/icons-material';
import {
  getPublicJobById,
  submitApplication,
  PublicJob,
  ApplicationData
} from '@/services/PublicJobService';

const PublicJobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [job, setJob] = useState<PublicJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState<ApplicationData>({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    cover_letter: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Nucleus brand colors
  const brandColors = {
    primary: '#262b45',
    accent: '#4f46e5',
    gradient: isDark
      ? 'linear-gradient(135deg, #1a1a2e 0%, #262b45 100%)'
      : 'linear-gradient(135deg, #262b45 0%, #4f46e5 100%)',
    buttonGradient: 'linear-gradient(135deg, #262b45 0%, #4f46e5 100%)',
    buttonHover: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
  };

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;
      setLoading(true);
      const response = await getPublicJobById(id);
      if (response.success && response.data) {
        setJob(response.data);
      } else {
        setError(response.error?.message || 'Job not found');
      }
      setLoading(false);
    };
    fetchJob();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, DOC, or DOCX file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size cannot exceed 5MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.applicant_name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.applicant_email.trim()) {
      setError('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.applicant_email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    const response = await submitApplication(id!, {
      ...formData,
      resume: selectedFile || undefined
    });

    if (response.success) {
      setSubmitted(true);
    } else {
      setError(response.error?.message || 'Failed to submit application');
    }

    setSubmitting(false);
  };

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return null;
    const format = (n: number) => n.toLocaleString('en-IN');
    if (min && max) return `₹${format(min)} - ₹${format(max)}`;
    if (min) return `From ₹${format(min)}`;
    return `Up to ₹${format(max!)}`;
  };

  const handleCopyLink = async () => {
    const publicUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareLinkedIn = () => {
    const publicUrl = encodeURIComponent(window.location.href);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${publicUrl}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: brandColors.accent }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading job details...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !job) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
        {/* Header */}
        <Box
          sx={{
            background: brandColors.gradient,
            color: 'white',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton color="inherit" onClick={() => navigate('/careers')} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box
                component="img"
                src="/assets/logos/logo-192.png"
                alt="Nucleus"
                sx={{ height: 32, width: 32 }}
              />
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                Multiplierskraft Careers
              </Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
              onClick={() => navigate('/login')}
            >
              Employee Login
            </Button>
          </Box>
        </Box>
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Work sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 2 }} />
          <Typography variant="h5" fontWeight="600" color="text.primary" gutterBottom>
            Job Not Found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This position may no longer be available
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/careers')}
            sx={{
              background: brandColors.buttonGradient,
              '&:hover': { background: brandColors.buttonHover }
            }}
          >
            View Open Positions
          </Button>
        </Container>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
        {/* Header */}
        <Box
          sx={{
            background: brandColors.gradient,
            color: 'white',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/assets/logos/logo-192.png"
              alt="Nucleus"
              sx={{ height: 32, width: 32 }}
            />
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
              Multiplierskraft Careers
            </Typography>
          </Box>
        </Box>
        <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <CheckCircle sx={{ fontSize: 60, color: theme.palette.success.main }} />
          </Box>
          <Typography variant="h4" fontWeight="800" gutterBottom sx={{ color: isDark ? theme.palette.text.primary : brandColors.primary }}>
            Application Submitted!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Thank you for applying to <strong>{job?.title}</strong>. We've received your application and will review it shortly.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/careers')}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: brandColors.buttonGradient,
              '&:hover': { background: brandColors.buttonHover }
            }}
          >
            Browse More Jobs
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Header */}
      <Box
        sx={{
          background: brandColors.gradient,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />

        {/* Nav */}
        <Box sx={{ py: 2, px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/careers')}
              sx={{ mr: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <ArrowBack />
            </IconButton>
            <Box
              component="img"
              src="/assets/logos/logo-192.png"
              alt="Nucleus"
              sx={{ height: 32, width: 32 }}
            />
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
              Multiplierskraft Careers
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            onClick={() => navigate('/login')}
          >
            Employee Login
          </Button>
        </Box>

        {/* Job Title Hero */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, position: 'relative' }}>
          <Typography
            variant="h3"
            fontWeight="800"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 1,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {job?.title}
          </Typography>
          {job?.department && (
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
              {job.department}
            </Typography>
          )}

          {/* Share Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Tooltip title={copied ? "Link Copied!" : "Copy Job Link"}>
              <Button
                variant="contained"
                size="small"
                startIcon={copied ? <Check /> : <ContentCopy />}
                onClick={handleCopyLink}
                sx={{
                  bgcolor: copied ? theme.palette.success.main : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: copied ? theme.palette.success.main : 'rgba(255,255,255,0.3)' },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </Tooltip>
            <Tooltip title="Share on LinkedIn">
              <Button
                variant="contained"
                size="small"
                startIcon={<LinkedIn />}
                onClick={handleShareLinkedIn}
                sx={{
                  bgcolor: '#0077b5',
                  color: 'white',
                  '&:hover': { bgcolor: '#006097' },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Share
              </Button>
            </Tooltip>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4, mt: -2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          {/* Job Details */}
          <Box>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                bgcolor: theme.palette.background.paper
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                  {job?.location && (
                    <Chip
                      icon={<LocationOn sx={{ fontSize: 18 }} />}
                      label={job.location}
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: theme.palette.info.main }
                      }}
                    />
                  )}
                  {job?.employment_type && (
                    <Chip
                      label={job.employment_type.replace('-', ' ')}
                      sx={{
                        bgcolor: alpha(brandColors.accent, 0.1),
                        color: brandColors.accent,
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}
                    />
                  )}
                  {job?.positions_count && (
                    <Chip
                      icon={<People sx={{ fontSize: 18 }} />}
                      label={`${job.positions_count} opening${job.positions_count > 1 ? 's' : ''}`}
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: theme.palette.success.main }
                      }}
                    />
                  )}
                  {formatSalary(job?.salary_min, job?.salary_max) && (
                    <Chip
                      icon={<AttachMoney sx={{ fontSize: 18 }} />}
                      label={formatSalary(job?.salary_min, job?.salary_max)}
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        color: theme.palette.warning.dark,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: theme.palette.warning.dark }
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: isDark ? theme.palette.text.primary : brandColors.primary }}>
                  Job Description
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', mb: 4, lineHeight: 1.8, color: 'text.secondary' }}
                >
                  {job?.description}
                </Typography>

                {job?.requirements && (
                  <>
                    <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: isDark ? theme.palette.text.primary : brandColors.primary }}>
                      Requirements
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'text.secondary' }}
                    >
                      {job.requirements}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Application Form */}
          <Box>
            <Card
              sx={{
                position: 'sticky',
                top: 20,
                borderRadius: 3,
                boxShadow: `0 8px 30px ${alpha(brandColors.accent, 0.15)}`,
                border: '1px solid',
                borderColor: brandColors.accent,
                overflow: 'hidden',
                bgcolor: theme.palette.background.paper
              }}
            >
              <Box
                sx={{
                  background: brandColors.buttonGradient,
                  color: 'white',
                  py: 2,
                  px: 3
                }}
              >
                <Typography variant="h6" fontWeight="700" sx={{ color: 'white' }}>
                  Apply for this Position
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <TextField
                    name="applicant_name"
                    label="Full Name"
                    value={formData.applicant_name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <TextField
                    name="applicant_email"
                    label="Email"
                    type="email"
                    value={formData.applicant_email}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <TextField
                    name="applicant_phone"
                    label="Phone (optional)"
                    value={formData.applicant_phone}
                    onChange={handleInputChange}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  {/* Resume Upload */}
                  <Box>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<CloudUpload />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        bgcolor: selectedFile ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                        borderColor: selectedFile ? theme.palette.success.main : theme.palette.divider,
                        color: selectedFile ? theme.palette.success.main : theme.palette.text.secondary,
                        '&:hover': {
                          borderStyle: 'dashed',
                          borderWidth: 2,
                          bgcolor: theme.palette.action.hover
                        }
                      }}
                    >
                      {selectedFile ? selectedFile.name : 'Upload Resume (PDF, DOC)'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Max file size: 5MB
                    </Typography>
                  </Box>

                  <TextField
                    name="cover_letter"
                    label="Cover Letter (optional)"
                    value={formData.cover_letter}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Tell us why you're interested in this role..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    sx={{
                      mt: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: brandColors.buttonGradient,
                      boxShadow: `0 4px 12px ${alpha(brandColors.primary, 0.4)}`,
                      '&:hover': {
                        background: brandColors.buttonHover
                      }
                    }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Application'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)'
            : 'linear-gradient(135deg, #262b45 0%, #1a1a2e 100%)',
          color: 'white',
          py: 6,
          mt: 6
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/assets/logos/logo-192.png"
                alt="Nucleus"
                sx={{ height: 32, width: 32, opacity: 0.9 }}
              />
              <Typography variant="h6" fontWeight="600" sx={{ color: 'white' }}>
                Nucleus
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
              Join us and be part of something great
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Powered by Nucleus HR
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicJobDetailPage;
